import { FunctionComponent, useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import BreadCumb from "../../components/common/Breadcrumb";
import {
  ApolloError,
  useLazyQuery,
  useMutation,
  useQuery,
} from "@apollo/client";
import { format } from "date-fns";
import backArrow from "../../assets/task/back_arrow.svg";
import figmaIconedit from "../../assets/task/edit-svgrepo-com.svg";

import {
  SCREENS_BY_PROJECT,
  EMPLOYEES_BY_AGENCY_ID,
  PODS_DATA,
  PROJECTS_BY_EMP_ID,
} from "../../api/query";
import { CREATE_SCREEN, CREATE_SCREEN_API, DELETE_SCREEN, DELETE_SCREEN_API } from "../../api/mutation";
import { useAuth } from "../../context/AuthContext";
import Loader from "../../components/common/Loader";
import {
  showError,
  showSuccess,
} from "../../components/common/ToastNotification";

// ─── Inline portal dropdown to avoid overflow clipping ───────────────────────
interface DropdownOption {
  key: string;
  name: string;
}

interface PortalDropdownProps {
  placeholder: string;
  value: string;
  options: DropdownOption[];
  onSelect: (key: string, name: string) => void;
}

const PortalDropdown: FunctionComponent<PortalDropdownProps> = ({
  placeholder,
  value,
  options,
  onSelect,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [rect, setRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(
    () =>
      options.filter((o) =>
        o.name.toLowerCase().includes(search.toLowerCase()),
      ),
    [options, search],
  );

  const handleOpen = () => {
    if (triggerRef.current) {
      setRect(triggerRef.current.getBoundingClientRect());
    }
    setOpen(true);
    setSearch("");
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node) &&
        listRef.current &&
        !listRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Reposition on scroll/resize
  useEffect(() => {
    if (!open) return;
    const update = () => {
      if (triggerRef.current)
        setRect(triggerRef.current.getBoundingClientRect());
    };
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  const displayValue = value || placeholder;

  return (
    <>
      <div
        ref={triggerRef}
        onClick={handleOpen}
        className={`flex items-center justify-between gap-2 w-full py-[9px] border rounded-md text-sm cursor-pointer select-none
 ${open ? "border-main-blue" : "border-surface-border dark:border-slate-600"}
 dark:bg-slate-700 dark:text-white`}
      >
        <span
          className={`truncate ${value ? "text-colors-text-black dark:text-white" : "text-text-text-secondary"}`}
        >
          {displayValue}
        </span>
        <svg
          className={`w-4 h-4 shrink-0 text-gray-400 transition-transform duration-150 ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {open && rect && (
        <div
          ref={listRef}
          style={{
            position: "fixed",
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width,
            zIndex: 9999,
          }}
          className="bg-white dark:bg-slate-800 border border-surface-border dark:border-slate-600 rounded-md shadow-lg overflow-hidden projectdropdown-item"
        >
          <div className="p-2 border-b border-surface-border dark:border-slate-700">
            <input
              autoFocus
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className=" px-2 py-1 text-sm border border-surface-border dark:border-slate-600 rounded dark:bg-slate-700 dark:text-white focus:outline-none focus:border-main-blue"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length > 0 ? (
              filtered.map((opt) => (
                <div
                  key={opt.key}
                  onMouseDown={() => {
                    onSelect(opt.key, opt.name);
                    setOpen(false);
                  }}
                  className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 dark:hover:bg-slate-700 dark:text-white
 ${value === opt.name ? "bg-blue-50 dark:bg-slate-700 font-semibold text-main-blue" : ""}`}
                >
                  {opt.name}
                </div>
              ))
            ) : (
              <div className="px-3 py-3 text-sm text-text-text-secondary text-center">
                No results found
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// ─── API Modal ────────────────────────────────────────────────────────────────
interface SavedApi {
  id: number;
  api_name: string;
  api_endpoint: string;
  description: string;
}

interface PendingApi {
  localId: string;
  api_name: string;
  api_endpoint: string;
  description: string;
}

interface ApiModalProps {
  screenId: number | null;
  employeeId: number;
  initialApis: SavedApi[];
  onClose: () => void;
  onSaveComplete: (updatedApis: SavedApi[]) => void;
}

const ApiModal: FunctionComponent<ApiModalProps> = ({ screenId, employeeId, initialApis, onClose, onSaveComplete }) => {
  const [savedApis, setSavedApis] = useState<SavedApi[]>(initialApis);
  const [pendingApis, setPendingApis] = useState<PendingApi[]>([]);
  const [selectedApi, setSelectedApi] = useState<SavedApi | null>(null);
  const [formName, setFormName] = useState("");
  const [formEndpoint, setFormEndpoint] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingApiId, setDeletingApiId] = useState<number | null>(null);
  const localCounter = useRef(0);
  const formNameRef = useRef<HTMLInputElement>(null);

  const [createScreenApi] = useMutation(CREATE_SCREEN_API, { fetchPolicy: "no-cache" });
  const [deleteScreenApi] = useMutation(DELETE_SCREEN_API, { fetchPolicy: "no-cache" });

  const handleDeleteSaved = async (api: SavedApi) => {
    setDeletingApiId(api.id);
    try {
      const res = await deleteScreenApi({ variables: { screen_api_id: Number(api.id) } });
      const result = res.data?.delete_screen_api;
      if (result?.status_code === 200 || result?.status_code === 201) {
        const updated = savedApis.filter((a) => a.id !== api.id);
        setSavedApis(updated);
        if (selectedApi?.id === api.id) setSelectedApi(null);
        onSaveComplete(updated);
        showSuccess(result.message || "API deleted");
      } else {
        showError(result?.message || "Failed to delete API");
      }
    } catch (error) {
      if (error instanceof ApolloError) showError(error.message);
    }
    setDeletingApiId(null);
  };

  const handleAdd = () => {
    const name = formName.trim();
    const endpoint = formEndpoint.trim();
    if (!name && !endpoint) {
      showError("Enter at least an API name or endpoint");
      return;
    }
    localCounter.current += 1;
    setPendingApis((prev) => [
      ...prev,
      { localId: String(localCounter.current), api_name: name, api_endpoint: endpoint, description: formDesc.trim() },
    ]);
    setFormName("");
    setFormEndpoint("");
    setFormDesc("");
    formNameRef.current?.focus();
  };

  const handleRemovePending = (localId: string) => {
    setPendingApis((prev) => prev.filter((p) => p.localId !== localId));
  };

  const handleSave = async () => {
    if (!screenId) { showError("Save the screen first before adding APIs"); return; }
    if (pendingApis.length === 0) { onClose(); return; }
    setSaving(true);
    try {
      const res = await createScreenApi({
        variables: {
          input: {
            screen_id: screenId ? Number(screenId) : screenId,
            employee_id: employeeId,
            apis: pendingApis.map((p) => ({
              id: null,
              api_name: p.api_name || null,
              api_endpoint: p.api_endpoint || null,
              description: p.description || null,
            })),
          },
        },
      });
      const result = res.data?.create_screen_api;
      if (result?.status_code === 200 || result?.status_code === 201) {
        const newlySaved: SavedApi[] = result.screen_apis ?? [];
        const updatedApis = [...savedApis, ...newlySaved];
        setSavedApis(updatedApis);
        setPendingApis([]);
        onSaveComplete(updatedApis);
        showSuccess(result.message || "APIs saved successfully");
      } else {
        showError(result?.message || "Failed to save APIs");
      }
    } catch (error) {
      if (error instanceof ApolloError) showError(error.message);
    }
    setSaving(false);
  };

  const inputCls = "px-3 py-2  border-[1px] border-solid border-surface-border  dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-700 dark:text-white placeholder-text-text-secondary focus:outline-none focus:border-main-blue focus:ring-1 focus:ring-main-blue/20 transition-colors";

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-lg mx-4 flex flex-col max-h-[90vh] overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-border dark:border-slate-700 shrink-0">
          <h2 className="text-base font-semibold text-colors-text-black dark:text-white m-0">API List</h2>
          <button
            onClick={onClose}
            className="text-gray-400 cta-red transition mt-0.5 ml-4 flex-shrink-0 pt-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-5">

          {/* ── Saved APIs ── */}
          {(savedApis.length > 0 || !screenId) && (
            <div>
              <p className="text-[11px] font-semibold text-text-text-secondary dark:text-gray-400 uppercase tracking-wider mb-2 m-0">
                Saved APIs
              </p>
              {!screenId && (
                <p className="text-xs text-amber-600 dark:text-amber-400">Save the screen first to add APIs.</p>
              )}
              {savedApis.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {savedApis.map((api) => {
                    const isSelected = selectedApi?.id === api.id;
                    const isDeleting = deletingApiId === api.id;
                    return (
                      <span
                        key={api.id}
                        className={`inline-flex items-center gap-1 pl-3 pr-1 py-1 text-sm rounded-full border transition-all
             ${isSelected
                            ? "bg-main-blue text-white border-main-blue shadow-sm"
                            : "bg-blue-50 dark:bg-slate-700 text-main-blue dark:text-blue-300 border-blue-200 dark:border-slate-600"
                          }`}
                      >
                        {/* Label + chevron — toggles detail */}
                        <button
                          onClick={() => setSelectedApi(isSelected ? null : api)}
                          className="inline-flex items-center gap-1 bg-transparent border-none cursor-pointer p-0 text-inherit"
                        >
                          <span className="max-w-[160px] truncate">{api.api_name || api.api_endpoint}</span>
                          <svg className="w-3 h-3 shrink-0 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isSelected ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                          </svg>
                        </button>
                        {/* Delete X */}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteSaved(api); }}
                          disabled={isDeleting}
                          title="Delete API"
                          className={`ml-0.5 p-0.5 rounded-full bg-transparent border-none cursor-pointer flex items-center transition-colors
              ${isSelected ? "text-white/70 hover:text-white hover:bg-white/20" : "text-main-blue/50 dark:text-blue-300/50 hover:text-red-500 dark:hover:text-red-400"}
              disabled:opacity-40 disabled:cursor-not-allowed`}
                        >
                          {isDeleting ? (
                            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                            </svg>
                          ) : (
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          )}
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              {savedApis.length === 0 && screenId && (
                <p className="text-xs text-text-text-secondary dark:text-gray-400">No APIs saved yet.</p>
              )}

              {/* Detail panel for selected saved API */}
              {selectedApi && (
                <div className="mt-3 p-3 rounded-lg bg-blue-50 dark:bg-slate-700 border border-blue-100 dark:border-slate-600 text-sm flex flex-col gap-1.5">
                  {selectedApi.api_name && (
                    <div className="flex gap-2">
                      <span className="font-semibold text-text-text-secondary dark:text-gray-400 w-16 shrink-0">Name</span>
                      <span className="text-colors-text-black dark:text-white break-all">{selectedApi.api_name}</span>
                    </div>
                  )}
                  {selectedApi.api_endpoint && (
                    <div className="flex gap-2">
                      <span className="font-semibold text-text-text-secondary dark:text-gray-400 w-16 shrink-0">URL</span>
                      <span className="text-colors-text-black dark:text-white break-all">{selectedApi.api_endpoint}</span>
                    </div>
                  )}
                  {/* {selectedApi.description && (
          <div className="flex gap-2">
           <span className="font-semibold text-text-text-secondary dark:text-gray-400 w-16 shrink-0">Desc</span>
           <span className="text-colors-text-black dark:text-white break-all">{selectedApi.description}</span>
          </div>
         )} */}
                </div>
              )}
            </div>
          )}

          {/* ── Pending (not yet saved) ── */}
          {pendingApis.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider mb-2 m-0">
                Pending · not saved yet
              </p>
              <div className="flex flex-wrap gap-2">
                {pendingApis.map((api) => (
                  <span
                    key={api.localId}
                    title={[api.api_name, api.api_endpoint, api.description].filter(Boolean).join(" · ")}
                    className="inline-flex items-center gap-1.5 px-3 py-1 text-sm rounded-full border border-dashed border-amber-400 dark:border-amber-500 bg-amber-50 dark:bg-slate-700 text-amber-700 dark:text-amber-300"
                  >
                    <span className="max-w-[180px] truncate">{api.api_name || api.api_endpoint}</span>
                    <button
                      onClick={() => handleRemovePending(api.localId)}
                      className="p-0 bg-transparent border-none cursor-pointer text-amber-500 hover:text-red-500 dark:text-amber-400 dark:hover:text-red-400 flex items-center transition-colors"
                      title="Remove"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ── Add form ── */}
          <div className="flex flex-col gap-3 self-stretch w-full">
            <div className="h-px bg-surface-border dark:bg-slate-700" />
            <p className="text-[11px] font-semibold text-text-text-secondary dark:text-gray-400 uppercase tracking-wider m-0">
              Add New API
            </p>
            <input
              ref={formNameRef}
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="API Name"
              className={inputCls}
            />
            <input
              type="text"
              value={formEndpoint}
              onChange={(e) => setFormEndpoint(e.target.value)}
              placeholder="API URL / Endpoint"
              className={inputCls}
            />
            <textarea
              value={formDesc}
              onChange={(e) => setFormDesc(e.target.value)}
              placeholder="Payload/Response (optional)"
              rows={2}
              className={inputCls + " resize-none"}
            />
            <div className="flex justify-end">
              <button
                onClick={handleAdd}
                className="px-4 py-2 text-sm font-medium text-white bg-main-blue hover:bg-dodgerblue rounded-md border-none cursor-pointer transition-colors"
              >
                Add
              </button>
            </div>
          </div>

        </div>{/* end scrollable body */}

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-surface-border dark:border-slate-700 shrink-0">
          <span className="text-xs text-text-text-secondary dark:text-gray-400">
            {pendingApis.length > 0 && `${pendingApis.length} pending`}
          </span>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-text-text-secondary dark:text-gray-300 bg-white dark:bg-slate-700 border border-surface-border dark:border-slate-600 rounded-md hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !screenId}
              className="px-4 py-2 text-sm font-medium text-white bg-main-blue hover:bg-dodgerblue rounded-md border-none cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Saving…" : `Save${pendingApis.length > 0 ? ` (${pendingApis.length})` : ""}`}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

// ─── Types ────────────────────────────────────────────────────────────────────
interface Screen {
  id: number | null;
  name: string;
  description: string;
  release_date: string;
  pod_id: number | null;
  pod_name: string;
  frontend_developer_id: number | null;
  frontend_developer_name: string;
  backend_developer_id: number | null;
  backend_developer_name: string;
  qa_id: number | null;
  qa_name: string;
  apis: SavedApi[];
  isNew?: boolean;
  isDirty?: boolean;
  figma_url?: any
}

// ─── Main Page ────────────────────────────────────────────────────────────────
const FeaturesAndDevelopers: FunctionComponent = () => {
  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const employeeID = userInfo?.employee_id;
  const agencyID = userInfo?.agencies?.id;

  const [screens, setScreens] = useState<Screen[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [pods, setPods] = useState<any[]>([]);
  const [apiModalRowIndex, setApiModalRowIndex] = useState<number | null>(null);
  const [figmaEditingIndex, setFigmaEditingIndex] = useState<number | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<any | null>(null);
  const [selectedProjectName, setSelectedProjectName] = useState<string>("");

  const [getProjects, { data: projectsData, loading: projectsLoading }] =
    useLazyQuery(PROJECTS_BY_EMP_ID, { fetchPolicy: "no-cache" });

  const [getScreens, { data: screensData, loading: screensLoading }] =
    useLazyQuery(SCREENS_BY_PROJECT, {
      fetchPolicy: "no-cache",
    });
  const [getEmployees, { data: employeesData, loading: employeesLoading }] =
    useLazyQuery(EMPLOYEES_BY_AGENCY_ID, {
      fetchPolicy: "no-cache",
    });
  const [createScreen, { loading: createScreenLoading }] = useMutation(
    CREATE_SCREEN,
    {
      fetchPolicy: "no-cache",
    },
  );
  const [deleteScreen] = useMutation(DELETE_SCREEN, { fetchPolicy: "no-cache" });

  const { data: podsData } = useQuery(PODS_DATA, {
    variables: {
      agency_id: parseInt(agencyID),
      start_date: format(new Date(), "yyyy-MM-dd"),
      end_date: format(new Date(), "yyyy-MM-dd"),
    },
    fetchPolicy: "no-cache",
    onCompleted: (da: any) => {
      if (da?.pods_data?.status_code === 200) {
        setPods(
          da?.pods_data?.data.filter(
            (pod: any) => pod.pod_name !== "All Pods"
          )
        );
      }
    },
  });

  const podOptions: DropdownOption[] = useMemo(
    () =>
      pods.map((pod: any) => ({
        key: String(pod.pod_id),
        name: pod.pod_name,
      })),
    [pods],
  );

  useEffect(() => {
    if (!agencyID) return;
    getEmployees({ variables: { agency_id: parseInt(agencyID) } });
    getProjects({ variables: { emp_id: "1000004", organization_id: String(agencyID) } });
  }, [agencyID]);

  useEffect(() => {
    const projects: any[] = projectsData?.projects_by_emp_id ?? [];
    if (projects.length === 0) return;
    const first = projects[14];
    setSelectedProjectId(first.id);
    setSelectedProjectName(first.name);
    getScreens({ variables: { project_id: parseInt(first.id) } });
  }, [projectsData]);

  useEffect(() => {
    if (screensData?.screens_by_project) {
      setScreens(
        screensData.screens_by_project.map((s: any) => ({
          ...s,
          apis: Array.isArray(s.apis) ? s.apis : [],
          isDirty: false,
        })),
      );
    }
  }, [screensData]);

  useEffect(() => {
    if (employeesData?.employees_by_agency_id?.status_code === 200) {
      setEmployees(employeesData.employees_by_agency_id.employees);
    }
  }, [employeesData]);

  useEffect(() => {
  const isModalOpen =
    apiModalRowIndex !== null || figmaEditingIndex !== null;

  if (!isModalOpen) return;

  const body = document.body;
  const html = document.documentElement;

  const originalBodyOverflow = body.style.overflow;
  const originalBodyPaddingRight = body.style.paddingRight;
  const originalHtmlOverflow = html.style.overflow;

  // Calculate scrollbar width
  const scrollbarWidth =
    window.innerWidth - document.documentElement.clientWidth;

  body.style.overflow = "hidden";
  html.style.overflow = "hidden";

  if (scrollbarWidth > 0) {
    body.style.paddingRight = `${scrollbarWidth}px`;
  }

  return () => {
    body.style.overflow = originalBodyOverflow;
    body.style.paddingRight = originalBodyPaddingRight;
    html.style.overflow = originalHtmlOverflow;
  };
}, [apiModalRowIndex, figmaEditingIndex]);


  const handleAddNewRow = () => {
    setScreens((prev) => [
      {
        id: null,
        name: "",
        description: "",
        release_date: "",

        pod_id: null,
        pod_name: "",

        frontend_developer_id: null,
        frontend_developer_name: "",

        backend_developer_id: null,
        backend_developer_name: "",

        qa_id: null,
        qa_name: "",

        apis: [],

        isNew: true,
        isDirty: true,
      },
      ...prev,
    ]);
  };
  const handleFieldChange = (
    index: number,
    field: keyof Screen,
    value: any,
  ) => {
    setScreens((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value, isDirty: true };
      return updated;
    });
  };

  const handleDropdownSelect = (
    index: number,
    field: "frontend" | "backend" | "qa" | "pod",
    key: string,
    name: string,
  ) => {
    setScreens((prev) => {
      const updated = [...prev];

      if (field === "frontend") {
        updated[index] = {
          ...updated[index],
          frontend_developer_id: parseInt(key),
          frontend_developer_name: name,
          isDirty: true,
        };
      } else if (field === "backend") {
        updated[index] = {
          ...updated[index],
          backend_developer_id: parseInt(key),
          backend_developer_name: name,
          isDirty: true,
        };
      } else if (field === "qa") {
        updated[index] = {
          ...updated[index],
          qa_id: parseInt(key),
          qa_name: name,
          isDirty: true,
        };
      } else {
        updated[index] = {
          ...updated[index],
          pod_id: parseInt(key),
          pod_name: name,
          isDirty: true,
        };
      }

      return updated;
    });
  };

  const handleSaveScreen = async (index: number) => {
    const screen = screens[index];
    if (!screen.name.trim()) {
      showError("Screen name is required");
      return;
    }

    const input: any = {
      name: screen.name,
      project_id: selectedProjectId ? parseInt(selectedProjectId) : '',
      description: screen.description || "",
      created_by: parseInt(employeeID),
    };
    if (screen.id) {
      input.id = parseInt(String(screen.id));
      input.updated_by = parseInt(employeeID);
    }
    if (screen.frontend_developer_id)
      input.frontend_developer_id = screen.frontend_developer_id;
    if (screen.backend_developer_id)
      input.backend_developer_id = screen.backend_developer_id;
    if (screen.qa_id) input.qa_id = screen.qa_id;
    if (screen.release_date) input.release_date = screen.release_date;
    if (screen.pod_id) {
      input.pod_id = screen.pod_id;
    } if (screen.figma_url) {
      input.figma_url = screen.figma_url;
    }

    try {
      const response = await createScreen({ variables: { input } });
      if (
        response.data.create_screen.status_code === 200 ||
        response.data.create_screen.status_code === 201
      ) {
        showSuccess(response.data.create_screen.message);
        await getScreens({ variables: { project_id: parseInt(selectedProjectId) } });
      } else {
        showError(response.data.create_screen.message);
      }
    } catch (error) {
      if (error instanceof ApolloError) showError(error.message);
    }
  };

  const handleDeleteScreen = async (index: number) => {
    const screen = screens[index];
    if (!screen.name.trim()) {
      showError("Screen name is required");
      return;
    }
    const id = screen?.id
    try {
      const res = await deleteScreen({ variables: { screen_id: Number(id), deleted_by: Number(employeeID) } });
      const result = res.data?.delete_screen;
      if (result?.status_code === 200 || result?.status_code === 201) {
        // Remove the deleted screen from local state
        setScreens((prev) => prev.filter((_, i) => i !== index));

        showSuccess(result.message || "Screen deleted");
      } else {
        showError(result?.message || "Failed to delete screen");
      }
    } catch (error) {
      if (error instanceof ApolloError) showError(error.message);
    }
  };

  const handleCancelNew = (index: number) => {
    setScreens((prev) => prev.filter((_, i) => i !== index));
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    try {
      return format(new Date(dateStr), "yyyy-MM-dd");
    } catch {
      return "";
    }
  };

  const projectOptions: DropdownOption[] = useMemo(
    () =>
      (projectsData?.projects_by_emp_id ?? []).map((p: any) => ({
        key: String(p.id),
        name: p.name,
      })),
    [projectsData],
  );

  const handleProjectSelect = (key: string, name: string) => {
    const id = parseInt(key);
    setSelectedProjectId(id);
    setSelectedProjectName(name);
    getScreens({ variables: { project_id: id } });
  };

  const isLoading = screensLoading || employeesLoading || createScreenLoading || projectsLoading;

  return (
    <div className="features_dev_tbl-dv w-full relative flex flex-col items-start justify-start min-w-[360px] leading-[normal] tracking-[normal] font-body-xs-semi-bold">
      <div className="self-stretch bg-gray min-h-screen w-full">
        <div
          id="cts-respon-p-dv"
          className="flex-1 flex flex-col items-start justify-start py-40 px-20 mq575:px-5 box-border gap-[20px] max-w-[100%] mq1425:max-w-full"
        >
          {/* ── Page Header ── */}
          <div className="grid gap-4  w-full">
            <BreadCumb />
            <div className="self-stretch flex flex-row items-center justify-between mq991:flex-wrap gap-3">

              <div className="flex flex-row items-center gap-3 ">
                <img
                  className="w-6 h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img"
                  alt="back"
                  src={backArrow}
                  onClick={() => navigate("/actions")}
                />
                <h3 className="m-0 text-xl font-bold text-colors-text-black dark:text-white">
                  Features &amp; Developers
                </h3>
              </div>
              <div className="flex flex-row items-center gap-3 ">
                {/* Project selector */}
                <div className="featuresdeve-dropdown">
                  <div className="w-52 mq575:w-32">
                    <PortalDropdown
                      placeholder="Select Project"
                      value={selectedProjectName}
                      options={projectOptions}
                      onSelect={handleProjectSelect}
                    />
                  </div>
                </div>
                <button
                  className="cursor-pointer flex items-center gap-2 py-2 px-4 bg-main-blue hover:bg-dodgerblue transition-colors rounded-lg border-none text-white text-sm font-semibold shadow-sm"
                  onClick={handleAddNewRow}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Add Screen
                </button>
              </div>
            </div>

          </div>

          {/* ── Table Card ── */}
          <div
            id=""
            className="bg-white w-full dark:bg-slate-700 flex flex-col items-start justify-start gap-[24px] client-report-dtnamic-tbl rounded-lg overflow-x-auto features_dev_tbl-main"
          >
            {/* Table header */}
            <table className="min-w-[100%] border-collapse table self-stretch border-[1px] border-solid border-surface-border rounded-lg overflow-x-auto dynamictabel-main-dv ">
              <thead className="bg-surface-bg-secondary dark:bg-slate-600">

                <tr>
                  {[
                    "S.N.",
                    "Screen Name",
                    "API",
                    "Pod",
                    "Team",
                    // "Frontend Dev",
                    // "Backend Dev",
                    // "QA",
                    "Release Date",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      className="relative p-4 text-sm dark:text-white tdth-custom-width"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>



              {/* Rows */}
              <tbody>
                {screens.map((screen, index) => {
                  return (
                    <tr
                      key={screen.id ?? `new-${index}`}
                      className={` ${screen.isNew ? " dark:bg-slate-750" : "hover:bg-gray-50 dark:hover:bg-slate-700"} transition-colors`}
                    >
                      <td className="tdth-custom-width dark:text-white text-sm relative">{index + 1}</td>
                      {/* Screen Name */}
                      <td className="tdth-custom-width dark:text-white text-sm relative">
                        <input
                          type="text"
                          value={screen.name}
                          onChange={(e) =>
                            handleFieldChange(index, "name", e.target.value)
                          }
                          placeholder="Screen name"
                          className="w-full px-3 py-[9px] border border-surface-border dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-700 dark:text-white placeholder-text-text-secondary focus:outline-none focus:border-main-blue focus:ring-1 focus:ring-main-blue/20 transition-colors"
                        />
                        {figmaEditingIndex === index ? (
                          <div className="flex items-center gap-1 mt-1">
                            <input
                              type="url"
                              value={screen.figma_url || ""}
                              onChange={(e) => handleFieldChange(index, "figma_url", e.target.value)}
                              placeholder="Paste Figma URL…"
                              autoFocus
                              className="flex-1 px-2 py-1 text-xs border border-main-blue rounded focus:outline-none dark:bg-slate-700 dark:text-white"
                            />
                            <button
                              onClick={() => setFigmaEditingIndex(null)}
                              title="Done"
                              className="p-1 text-green-600 hover:text-green-700 bg-transparent border-none cursor-pointer shrink-0"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 mt-1.5 figma-linkitems-dv">
                            {screen.figma_url ? (
                              <>
                                <a
                                  href={screen.figma_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-main-blue bg-blue-50 dark:bg-slate-700 border border-blue-200 dark:border-slate-500 rounded hover:bg-blue-100 dark:hover:bg-slate-600 transition-colors"
                                >
                                  <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                  Figma
                                </a>
                                <button
                                  onClick={() => setFigmaEditingIndex(index)}
                                  title="Edit Figma URL"
                                  className="p-1 text-gray-400 hover:text-main-blue bg-transparent border border-surface-border dark:border-slate-500 rounded cursor-pointer transition-colors"
                                >
                                  <img src={figmaIconedit} className="w-4 h-4" alt="" />
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => setFigmaEditingIndex(index)}
                                title="Add Figma URL"
                                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-text-text-secondary dark:text-gray-400 border border-dashed border-surface-border dark:border-slate-500 rounded hover:border-main-blue hover:text-main-blue bg-transparent cursor-pointer transition-colors"
                              >
                                <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Add Figma
                              </button>
                            )}
                          </div>
                        )}
                      </td>

                      {/* API */}
                      <td className="tdth-custom-width dark:text-white text-sm">
                        <button
                          onClick={() => setApiModalRowIndex(index)}
                          className="px-3 py-[7px] border border-surface-border dark:border-slate-600 rounded-md text-sm font-medium transition-colors cursor-pointer bg-white dark:bg-slate-700 dark:text-white hover:bg-blue-50 dark:hover:bg-slate-600 whitespace-nowrap"
                        >
                          {screen.apis && screen.apis.length > 0 ? "View / Edit" : "Add"}
                        </button>
                      </td>

                      <td className="tdth-custom-width dark:text-white text-sm">
                        <PortalDropdown
                          placeholder="Select Pod"
                          value={screen.pod_name}
                          options={podOptions}
                          onSelect={(k, n) =>
                            handleDropdownSelect(index, "pod", k, n)
                          }
                        />
                      </td>

                      <td className="tdth-custom-width dark:text-white text-sm ">
                        <input
                          type="text"
                          value={podsData?.pods_data?.data?.find((it: any) => it.pod_name == screen.pod_name)?.members.map?.((k: any) => " " + k.first_name + " " + k.last_name + ",").join("")}
                          placeholder="Pod Team"
                          className="w-full px-3 py-[9px] border border-surface-border dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-700 dark:text-white placeholder-text-text-secondary focus:outline-none focus:border-main-blue focus:ring-1 focus:ring-main-blue/20 transition-colors"
                        />
                      </td>


                      {/* Release Date */}
                      <td className="tdth-custom-width dark:text-white text-sm ">
                        <input
                          type="date"
                          value={formatDate(screen.release_date)}
                          onChange={(e) =>
                            handleFieldChange(
                              index,
                              "release_date",
                              e.target.value,
                            )
                          }
                          className="w-full px-3 py-[9px] border border-surface-border dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-700 dark:text-white focus:outline-none focus:border-main-blue focus:ring-1 focus:ring-main-blue/20 transition-colors"
                        />
                      </td>

                      {/* Actions */}
                      <td className="tdth-custom-width dark:text-white text-sm ">
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleSaveScreen(index)}
                            className=" px-3 py-[7px] bg-main-blue h-[37px] hover:bg-dodgerblue text-white rounded-md text-sm font-medium transition-colors border-none cursor-pointer  "
                          >
                            Save
                          </button>
                          {
                            !screen.isNew && <button
                              onClick={() => handleDeleteScreen(index)}
                              className=" text-gray-400 cta-red transition h-[37px] flex-shrink-0 pt-1"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          }
                          {screen.isNew && (
                            <button
                              onClick={() => handleCancelNew(index)}
                              className=" text-gray-400 cta-red transition h-[37px] flex-shrink-0 pt-1" title="Cancel"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          )}

                        </div>

                      </td>
                    </tr>
                  )
                })}
              </tbody>

            </table>
            {/* Empty state */}
            {screens.length === 0 && !screensLoading && (
              <div className="flex flex-col items-center justify-center py-16 gap-3 text-text-text-secondary dark:text-gray-400 featuresdev-item-dv">
                <svg
                  className="w-10 h-10 opacity-40"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
                  />
                </svg>
                <p className="text-sm">
                  No screens yet. Click <strong>Add Screen</strong> to get
                  started.
                </p>
              </div>
            )}

          </div>

          {/* Row count */}
          {screens.length > 0 && (
            <p className="text-xs text-text-text-secondary dark:text-gray-400 m-0">
              {screens.length} screen{screens.length !== 1 ? "s" : ""}
            </p>
          )}
        </div>
      </div>

      {isLoading && <Loader />}

      {apiModalRowIndex !== null && (
        <ApiModal
          screenId={screens[apiModalRowIndex]?.id ?? null}
          employeeId={parseInt(employeeID)}
          initialApis={screens[apiModalRowIndex]?.apis ?? []}
          onClose={() => setApiModalRowIndex(null)}
          onSaveComplete={(updatedApis) => {
            setScreens((prev) => {
              const updated = [...prev];
              if (apiModalRowIndex !== null) {
                updated[apiModalRowIndex] = { ...updated[apiModalRowIndex], apis: updatedApis };
              }
              return updated;
            });
          }}
        />
      )}
    </div>
  );
};

export default FeaturesAndDevelopers;